import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { PublicLayout } from './PublicLayout';
import { useAuth } from './AuthContext';
import { authAPI, referralAPI } from './api';
import { DEV_TEST_USERS, getDefaultRouteForUser, shouldShowDevAuthHints } from './services/authService';
import './pages.css';

interface GoogleCredentialResponse {
  credential?: string;
}

interface GoogleIdentityApi {
  initialize: (options: {
    client_id: string;
    callback: (response: GoogleCredentialResponse) => void;
    auto_select?: boolean;
    cancel_on_tap_outside?: boolean;
  }) => void;
  renderButton: (
    parent: HTMLElement,
    options: {
      type: 'standard';
      theme: 'outline';
      size: 'large';
      text: 'signup_with' | 'signin_with';
      shape: 'rectangular';
      logo_alignment: 'left';
      width: number;
      locale: string;
    },
  ) => void;
}

declare global {
  interface Window {
    google?: {
      accounts: {
        id: GoogleIdentityApi;
      };
    };
  }
}

let googleIdentityScriptPromise: Promise<void> | null = null;

const loadGoogleIdentityScript = () => {
  if (window.google?.accounts?.id) {
    return Promise.resolve();
  }
  if (googleIdentityScriptPromise) {
    return googleIdentityScriptPromise;
  }

  googleIdentityScriptPromise = new Promise<void>((resolve, reject) => {
    const existingScript = document.getElementById('google-identity-services');
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(), { once: true });
      existingScript.addEventListener('error', () => reject(new Error('Google kon niet worden geladen.')), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.id = 'google-identity-services';
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Google kon niet worden geladen.'));
    document.head.appendChild(script);
  });

  return googleIdentityScriptPromise;
};

const GOOGLE_REGISTRATION_CREDENTIAL_KEY = 'perfectodds_google_registration_credential';
const REFERRAL_CODE_STORAGE_KEY = 'perfectodds_referral_code';
const prepareGoogleButton = (container: HTMLDivElement) => {
  const availableWidth = Math.floor(container.getBoundingClientRect().width);
  const nativeWidth = Math.min(400, Math.max(240, availableWidth));
  const scale = availableWidth > nativeWidth ? availableWidth / nativeWidth : 1;

  container.style.setProperty('--google-button-scale', scale.toFixed(4));
  container.style.height = `${Math.ceil(40 * scale)}px`;
  return nativeWidth;
};

const GoogleMark: React.FC = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M21.6 12.23c0-.71-.06-1.4-.19-2.07H12v3.91h5.38a4.6 4.6 0 0 1-2 3.02v2.54h3.24c1.9-1.75 2.98-4.33 2.98-7.4Z" />
    <path fill="#34A853" d="M12 22c2.7 0 4.98-.9 6.63-2.43l-3.24-2.54c-.9.6-2.05.96-3.39.96-2.61 0-4.82-1.76-5.61-4.13H3.05v2.62A10 10 0 0 0 12 22Z" />
    <path fill="#FBBC05" d="M6.39 13.86A6.02 6.02 0 0 1 6.07 12c0-.65.11-1.28.32-1.86V7.52H3.05A10 10 0 0 0 2 12c0 1.61.38 3.14 1.05 4.48l3.34-2.62Z" />
    <path fill="#EA4335" d="M12 6.01c1.47 0 2.79.51 3.83 1.5l2.87-2.88A9.62 9.62 0 0 0 12 2a10 10 0 0 0-8.95 5.52l3.34 2.62C7.18 7.77 9.39 6.01 12 6.01Z" />
  </svg>
);

const AuthShowcase: React.FC = () => (
  <aside className="register-showcase">
    <Link to="/" className="register-showcase-logo" aria-label="PerfectOddsNL home">
      <img src="/assets/light-logo-POnl.png" alt="" />
      <span>PerfectOddsNL</span>
    </Link>

    <div className="register-showcase-copy">
      <span className="register-showcase-kicker">Start nu met surebetten</span>
      <h1>Gebruik wiskunde. Niet geluk.</h1>
      <p>Ontvang kansen via Telegram en houd iedere geplaatste surebet overzichtelijk bij.</p>

      <ul>
        <li><span>✓</span> Live en pre-match meldingen</li>
        <li><span>✓</span> Persoonlijke Surebet Tracker</li>
        <li><span>✓</span> Resultaten, ROI en kosten bij elkaar</li>
      </ul>
    </div>

    <div className="register-signal-preview">
      <div className="register-signal-topline">
        <span>Nieuwe surebet</span>
        <strong>Live</strong>
      </div>
      <p>FC Barcelona vs. Real Madrid</p>
      <div>
        <span>Verwachte ROI</span>
        <strong>+8.4%</strong>
      </div>
    </div>

    <p className="register-showcase-footnote">Eén account voor Telegram, je abonnement en je tracker.</p>
  </aside>
);

const AuthCard: React.FC<{ title: string; text: string; children: React.ReactNode }> = ({ title, text, children }) => (
  <PublicLayout>
    <div className="auth-container">
      <div className="auth-card">
        <div className="brand-header">
          <img src="/assets/dark-logo-POnl.png" alt="PerfectOddsNL logo" className="brand-logo logo-dark-bg" />
          <img src="/assets/light-logo-POnl.png" alt="PerfectOddsNL logo" className="brand-logo logo-light-bg" />
          <h1>PerfectOddsNL</h1>
        </div>

        <h2>{title}</h2>
        <p className="auth-intro">{text}</p>

        {children}
      </div>
    </div>
  </PublicLayout>
);

export const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [googleClientId, setGoogleClientId] = useState('');
  const [googleConfigLoaded, setGoogleConfigLoaded] = useState(false);
  const googleButtonRef = useRef<HTMLDivElement | null>(null);
  const { login, loginWithGoogle, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const redirectParam = new URLSearchParams(location.search).get('redirect');
  const stateFrom = typeof location.state === 'object' && location.state && 'from' in location.state ? String(location.state.from || '') : '';
  const postLoginDestination = redirectParam || stateFrom;

  useEffect(() => {
    if (user) {
      navigate(postLoginDestination || getDefaultRouteForUser(user), { replace: true });
    }
  }, [navigate, postLoginDestination, user]);

  useEffect(() => {
    let isMounted = true;

    authAPI.getGoogleConfig()
      .then((response) => {
        if (!isMounted) return;
        const clientId = typeof response.data?.clientId === 'string' ? response.data.clientId : '';
        setGoogleClientId(response.data?.enabled ? clientId : '');
      })
      .catch(() => {
        if (isMounted) setGoogleClientId('');
      })
      .finally(() => {
        if (isMounted) setGoogleConfigLoaded(true);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!googleClientId || !googleButtonRef.current) return;
    let isMounted = true;

    loadGoogleIdentityScript()
      .then(() => {
        if (!isMounted || !window.google?.accounts?.id || !googleButtonRef.current) return;
        const buttonWidth = prepareGoogleButton(googleButtonRef.current);
        window.google.accounts.id.initialize({
          client_id: googleClientId,
          auto_select: false,
          cancel_on_tap_outside: true,
          callback: async (response) => {
            if (!response.credential) {
              setError('Google heeft geen geldige aanmelding teruggegeven.');
              return;
            }

            setError('');
            setIsGoogleLoading(true);
            try {
              const currentUser = await loginWithGoogle(response.credential);
              navigate(postLoginDestination || getDefaultRouteForUser(currentUser), { replace: true });
            } catch (err) {
              setError(err instanceof Error ? err.message : 'Inloggen met Google is mislukt.');
            } finally {
              setIsGoogleLoading(false);
            }
          },
        });
        googleButtonRef.current.innerHTML = '';
        window.google.accounts.id.renderButton(googleButtonRef.current, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text: 'signin_with',
          shape: 'rectangular',
          logo_alignment: 'left',
          width: buttonWidth,
          locale: 'nl',
        });
      })
      .catch((scriptError) => {
        if (isMounted) {
          setError(scriptError instanceof Error ? scriptError.message : 'Google kon niet worden geladen.');
        }
      });

    return () => {
      isMounted = false;
    };
  }, [googleClientId, loginWithGoogle, navigate, postLoginDestination]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const currentUser = await login(email, password);
      navigate(postLoginDestination || getDefaultRouteForUser(currentUser), { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Onjuiste e-mail of wachtwoord.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PublicLayout>
      <div className="register-page login-page">
        <section className="register-shell login-register-shell">
          <AuthShowcase />

          <section className="register-form-panel login-form-panel">
            <div className="register-form-heading">
              <span className="register-form-kicker">Welkom terug</span>
              <h2>Log in op je account</h2>
              <p>Ga verder met je meldingen, abonnement en Surebet Tracker.</p>
            </div>

            {error && <div className="error-message register-error" role="alert">{error}</div>}

            <form onSubmit={handleSubmit} className="auth-form register-form login-form">
              <div className="form-group">
                <label htmlFor="login-email">E-mailadres</label>
                <input
                  id="login-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  placeholder="jij@voorbeeld.nl"
                />
              </div>

              <div className="form-group">
                <div className="login-password-label">
                  <label htmlFor="login-password">Wachtwoord</label>
                  <Link to="/wachtwoord-vergeten">Wachtwoord vergeten?</Link>
                </div>
                <input
                  id="login-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  placeholder="Je wachtwoord"
                />
              </div>

              <button type="submit" className="register-submit" disabled={isLoading || isGoogleLoading} aria-busy={isLoading}>
                {isLoading ? 'Bezig met inloggen...' : 'Inloggen'}
                {!isLoading && <span aria-hidden="true">→</span>}
              </button>
            </form>

            <div className="register-divider"><span>of</span></div>

            <div className="register-google-slot ready">
              {googleClientId ? (
                <div ref={googleButtonRef} className="register-google-render" aria-label="Inloggen met Google" />
              ) : (
                <button type="button" className="register-google-fallback" disabled>
                  <GoogleMark />
                  {!googleConfigLoaded ? 'Google laden...' : 'Inloggen met Google'}
                </button>
              )}
              {isGoogleLoading && <small>Google-account wordt gecontroleerd...</small>}
            </div>

            <p className="register-login-link">
              Nog geen account? <Link to="/register">Registreren</Link>
            </p>

            {shouldShowDevAuthHints && DEV_TEST_USERS.length > 0 && (
              <section className="test-account-card card">
                <h3>Testaccounts</h3>
                <ul className="test-account-list">
                  {DEV_TEST_USERS.map((account) => (
                    <li key={account.email}>
                      <strong>{account.name}:</strong> {account.email} / {account.password}
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </section>
        </section>
      </div>
    </PublicLayout>
  );
};

export const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (!email.trim()) {
      setError('Vul je e-mailadres in.');
      return;
    }

    setIsLoading(true);
    try {
      await authAPI.forgotPassword(email.trim());
      setMessage('Als dit e-mailadres bestaat, sturen we een eenmalige code.');
      navigate('/wachtwoord-resetten', { state: { email: email.trim() } });
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Herstelcode aanvragen is mislukt.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthCard
      title="Wachtwoord vergeten"
      text="Vul je e-mailadres in. Je ontvangt een eenmalige code waarmee je je wachtwoord kunt herstellen."
    >
      {error && <div className="error-message" role="alert">{error}</div>}
      {message && <div className="success-message" role="status">{message}</div>}

      <form onSubmit={handleSubmit} className="auth-form">
        <div className="form-group">
          <label htmlFor="forgot-email">E-mailadres</label>
          <input
            id="forgot-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            placeholder="naam@perfectodds.nl"
          />
        </div>
        <button type="submit" disabled={isLoading} aria-busy={isLoading}>
          {isLoading ? 'Code versturen...' : 'Stuur herstelcode'}
        </button>
      </form>

      <p className="switch-auth">
        Code al ontvangen? <Link to="/wachtwoord-resetten">Wachtwoord herstellen</Link>
      </p>
      <p className="switch-auth">
        Terug naar <Link to="/login">inloggen</Link>
      </p>
    </AuthCard>
  );
};

export const ResetPasswordPage: React.FC = () => {
  const location = useLocation();
  const stateEmail = typeof location.state === 'object' && location.state && 'email' in location.state ? String(location.state.email || '') : '';
  const [email, setEmail] = useState(stateEmail);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (!email.trim() || !code.trim() || !password.trim() || !confirmPassword.trim()) {
      setError('Vul alle velden in.');
      return;
    }
    if (password !== confirmPassword) {
      setError('De wachtwoorden komen niet overeen.');
      return;
    }
    if (password.trim().length < 6) {
      setError('Wachtwoord moet minimaal 6 tekens bevatten.');
      return;
    }

    setIsLoading(true);
    try {
      await authAPI.resetPassword({ email: email.trim(), code: code.trim(), password });
      setMessage('Je wachtwoord is gewijzigd. Je kunt nu inloggen.');
      setTimeout(() => navigate('/login'), 900);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Wachtwoord herstellen is mislukt.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthCard
      title="Wachtwoord herstellen"
      text="Vul de eenmalige code uit je e-mail in en kies een nieuw wachtwoord."
    >
      {error && <div className="error-message" role="alert">{error}</div>}
      {message && <div className="success-message" role="status">{message}</div>}

      <form onSubmit={handleSubmit} className="auth-form">
        <div className="form-group">
          <label htmlFor="reset-email">E-mailadres</label>
          <input id="reset-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="naam@perfectodds.nl" />
        </div>
        <div className="form-group">
          <label htmlFor="reset-code">Eenmalige code</label>
          <input id="reset-code" inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} required placeholder="123456" />
        </div>
        <div className="form-group">
          <label htmlFor="reset-password">Nieuw wachtwoord</label>
          <input id="reset-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="••••••••" />
        </div>
        <div className="form-group">
          <label htmlFor="reset-confirm-password">Nieuw wachtwoord herhalen</label>
          <input id="reset-confirm-password" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required placeholder="••••••••" />
        </div>
        <button type="submit" disabled={isLoading} aria-busy={isLoading}>
          {isLoading ? 'Wachtwoord herstellen...' : 'Wachtwoord herstellen'}
        </button>
      </form>

      <p className="switch-auth">
        Geen code? <Link to="/wachtwoord-vergeten">Nieuwe code aanvragen</Link>
      </p>
    </AuthCard>
  );
};

export const RegisterPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [googleClientId, setGoogleClientId] = useState('');
  const [googleConfigLoaded, setGoogleConfigLoaded] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [referralState, setReferralState] = useState<'none' | 'checking' | 'valid' | 'invalid'>('none');
  const googleButtonRef = useRef<HTMLDivElement | null>(null);
  const acceptedTermsRef = useRef(acceptedTerms);
  const { register, registerWithGoogle, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const queryCode = new URLSearchParams(location.search).get('ref')?.trim().toUpperCase() || '';
    if (!queryCode) {
      setReferralCode('');
      setReferralState('none');
      window.sessionStorage.removeItem(REFERRAL_CODE_STORAGE_KEY);
      return;
    }

    setReferralCode(queryCode);
    setReferralState('checking');
    window.sessionStorage.setItem(REFERRAL_CODE_STORAGE_KEY, queryCode);
    let cancelled = false;
    referralAPI.validate(queryCode)
      .then((response) => {
        if (cancelled) return;
        if (response.data?.valid) {
          setReferralState('valid');
          window.sessionStorage.setItem(REFERRAL_CODE_STORAGE_KEY, queryCode);
        } else {
          setReferralState('invalid');
          setReferralCode('');
          window.sessionStorage.removeItem(REFERRAL_CODE_STORAGE_KEY);
        }
      })
      .catch(() => {
        if (cancelled) return;
        setReferralState('invalid');
        setReferralCode('');
        window.sessionStorage.removeItem(REFERRAL_CODE_STORAGE_KEY);
      });
    return () => { cancelled = true; };
  }, [location.search]);

  useEffect(() => {
    acceptedTermsRef.current = acceptedTerms;
  }, [acceptedTerms]);

  useEffect(() => {
    if (user) {
      navigate(getDefaultRouteForUser(user), { replace: true });
    }
  }, [navigate, user]);

  useEffect(() => {
    let isMounted = true;

    authAPI.getGoogleConfig()
      .then((response) => {
        if (!isMounted) return;
        const clientId = typeof response.data?.clientId === 'string' ? response.data.clientId : '';
        setGoogleClientId(response.data?.enabled ? clientId : '');
      })
      .catch(() => {
        if (isMounted) setGoogleClientId('');
      })
      .finally(() => {
        if (isMounted) setGoogleConfigLoaded(true);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const canUseGoogle = Boolean(googleClientId);

  useEffect(() => {
    if (!canUseGoogle || !googleButtonRef.current) return;
    let isMounted = true;

    loadGoogleIdentityScript()
      .then(() => {
        if (!isMounted || !window.google?.accounts?.id || !googleButtonRef.current) return;
        const buttonWidth = prepareGoogleButton(googleButtonRef.current);
        window.google.accounts.id.initialize({
          client_id: googleClientId,
          auto_select: false,
          cancel_on_tap_outside: true,
          callback: async (response) => {
            if (!response.credential) {
              setError('Google heeft geen geldige aanmelding teruggegeven.');
              return;
            }
            if (!acceptedTermsRef.current) {
              window.sessionStorage.setItem(GOOGLE_REGISTRATION_CREDENTIAL_KEY, response.credential);
              navigate('/registreren/afronden', {
                state: { googleCredential: response.credential },
              });
              return;
            }

            setError('');
            setIsGoogleLoading(true);
            try {
              await registerWithGoogle(
                response.credential,
                referralState === 'valid' ? referralCode : undefined,
              );
              window.sessionStorage.removeItem(REFERRAL_CODE_STORAGE_KEY);
              navigate('/mijn-account', {
                replace: true,
                state: { message: 'Je account is via Google aangemaakt.' },
              });
            } catch (err) {
              setError(err instanceof Error ? err.message : 'Registreren met Google is mislukt.');
            } finally {
              setIsGoogleLoading(false);
            }
          },
        });
        googleButtonRef.current.innerHTML = '';
        window.google.accounts.id.renderButton(googleButtonRef.current, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text: 'signup_with',
          shape: 'rectangular',
          logo_alignment: 'left',
          width: buttonWidth,
          locale: 'nl',
        });
      })
      .catch((scriptError) => {
        if (isMounted) {
          setError(scriptError instanceof Error ? scriptError.message : 'Google kon niet worden geladen.');
        }
      });

    return () => {
      isMounted = false;
    };
  }, [canUseGoogle, googleClientId, navigate, referralCode, referralState, registerWithGoogle]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email.trim() || !password.trim() || !confirmPassword.trim()) {
      setError('Vul alle velden in.');
      return;
    }

    if (password !== confirmPassword) {
      setError('De wachtwoorden komen niet overeen.');
      return;
    }

    if (password.trim().length < 6) {
      setError('Wachtwoord moet minimaal 6 tekens bevatten.');
      return;
    }

    if (!acceptedTerms) {
      setError('Accepteer de algemene voorwaarden en het privacybeleid om verder te gaan.');
      return;
    }

    setIsLoading(true);

    try {
      await register(email, password, referralState === 'valid' ? referralCode : undefined);
      window.sessionStorage.removeItem(REFERRAL_CODE_STORAGE_KEY);
      navigate('/mijn-account', {
        replace: true,
        state: {
          message: 'Je account is aangemaakt. Kies een abonnement om toegang te krijgen tot Mijn tracker.',
        },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registratie mislukt.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PublicLayout>
      <div className="register-page">
        <section className="register-shell">
          <AuthShowcase />

          <section className="register-form-panel">
            <div className="register-form-heading">
              <span className="register-form-kicker">Gratis account</span>
              <h2>Maak je account aan</h2>
              <p>Je kiest pas na registratie welk abonnement bij je past.</p>
            </div>

            {referralState === 'valid' && (
              <div className="register-referral-banner" role="status">
                <span aria-hidden="true">€10</span>
                <div>
                  <strong>Je vriendenvoordeel staat klaar</strong>
                  <p>Ontvang automatisch €10 korting op je eerste geschikte maandabonnement.</p>
                </div>
              </div>
            )}
            {referralState === 'checking' && referralCode && (
              <div className="register-referral-banner register-referral-banner--checking" role="status">
                <span aria-hidden="true">…</span><div><strong>Vriendenlink controleren</strong></div>
              </div>
            )}
            {referralState === 'invalid' && (
              <div className="error-message register-error" role="alert">Deze vriendenlink is niet meer geldig. Je kunt wel gewoon registreren.</div>
            )}

            {error && <div className="error-message register-error" role="alert">{error}</div>}

            <form onSubmit={handleSubmit} className="auth-form register-form">
              <div className="form-group">
                <label htmlFor="register-email">E-mailadres</label>
                <input
                  id="register-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="jij@voorbeeld.nl"
                  autoComplete="email"
                  required
                />
              </div>

              <div className="register-password-grid">
                <div className="form-group">
                  <label htmlFor="register-password">Wachtwoord</label>
                  <input
                    id="register-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Minimaal 6 tekens"
                    autoComplete="new-password"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="register-confirm-password">Herhaal wachtwoord</label>
                  <input
                    id="register-confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Nogmaals je wachtwoord"
                    autoComplete="new-password"
                    required
                  />
                </div>
              </div>

              <label className="register-consent">
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  required
                />
                <span>
                  Ik ga akkoord met de <Link to="/algemene-voorwaarden?from=register">algemene voorwaarden</Link> en het{' '}
                  <Link to="/privacybeleid?from=register">privacybeleid</Link>.
                </span>
              </label>

              <button type="submit" className="register-submit" disabled={isLoading || isGoogleLoading} aria-busy={isLoading}>
                {isLoading ? 'Account wordt aangemaakt...' : 'Account aanmaken'}
                {!isLoading && <span aria-hidden="true">→</span>}
              </button>
            </form>

            <div className="register-divider"><span>of</span></div>

            <div className={`register-google-slot${canUseGoogle ? ' ready' : ''}`}>
              {canUseGoogle ? (
                <div ref={googleButtonRef} className="register-google-render" aria-label="Registreren met Google" />
              ) : (
                <button
                  type="button"
                  className="register-google-fallback"
                  disabled={!googleConfigLoaded || Boolean(googleClientId)}
                  onClick={() => setError('Google-registratie heeft nog een Google OAuth-client-ID nodig.')}
                >
                  <GoogleMark />
                  {!googleConfigLoaded ? 'Google laden...' : 'Registreren met Google'}
                </button>
              )}
              {!googleClientId && googleConfigLoaded && (
                <small>Google-registratie wordt geactiveerd zodra de OAuth-client is gekoppeld.</small>
              )}
            </div>

            <p className="register-login-link">
              Al een account? <Link to="/login">Inloggen</Link>
            </p>
          </section>
        </section>
      </div>
    </PublicLayout>
  );
};

export const CompleteGoogleRegistrationPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { registerWithGoogle, user } = useAuth();
  const stateCredential = typeof location.state === 'object'
    && location.state
    && 'googleCredential' in location.state
    ? String(location.state.googleCredential || '')
    : '';
  const credential = stateCredential || window.sessionStorage.getItem(GOOGLE_REGISTRATION_CREDENTIAL_KEY) || '';
  const storedReferralCode = window.sessionStorage.getItem(REFERRAL_CODE_STORAGE_KEY)?.trim().toUpperCase() || '';
  const [referralCode, setReferralCode] = useState(storedReferralCode);
  const [referralState, setReferralState] = useState<'none' | 'checking' | 'valid' | 'invalid'>(
    storedReferralCode ? 'checking' : 'none',
  );
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (user) {
      navigate(getDefaultRouteForUser(user), { replace: true });
    }
  }, [navigate, user]);

  useEffect(() => {
    if (!storedReferralCode) return;
    let cancelled = false;
    referralAPI.validate(storedReferralCode)
      .then((response) => {
        if (cancelled) return;
        if (response.data?.valid) {
          setReferralState('valid');
          return;
        }
        setReferralState('invalid');
        setReferralCode('');
        window.sessionStorage.removeItem(REFERRAL_CODE_STORAGE_KEY);
      })
      .catch(() => {
        if (cancelled) return;
        setReferralState('invalid');
        setReferralCode('');
        window.sessionStorage.removeItem(REFERRAL_CODE_STORAGE_KEY);
      });
    return () => { cancelled = true; };
  }, [storedReferralCode]);

  const handleCompleteGoogleRegistration = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');

    if (!credential) {
      setError('Je Google-aanmelding is verlopen. Start de registratie opnieuw.');
      return;
    }
    if (!acceptedTerms) {
      setError('Accepteer de algemene voorwaarden en het privacybeleid om verder te gaan.');
      return;
    }

    setIsLoading(true);
    try {
      await registerWithGoogle(credential, referralState === 'valid' ? referralCode : undefined);
      window.sessionStorage.removeItem(GOOGLE_REGISTRATION_CREDENTIAL_KEY);
      window.sessionStorage.removeItem(REFERRAL_CODE_STORAGE_KEY);
      navigate('/mijn-account', {
        replace: true,
        state: { message: 'Je account is via Google aangemaakt.' },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Google-registratie afronden is mislukt.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PublicLayout>
      <div className="register-page google-complete-page">
        <section className="register-shell google-complete-shell">
          <AuthShowcase />

          <section className="register-form-panel google-complete-panel">
            <div className="register-form-heading">
              <span className="register-form-kicker">Laatste stap</span>
              <h2>Rond je registratie af</h2>
              <p>Bevestig de voorwaarden om je account aan te maken.</p>
            </div>

            <div className="google-complete-account">
              <GoogleMark />
              <div>
                <strong>Google-account geselecteerd</strong>
                <span>Je e-mailadres wordt veilig door Google bevestigd.</span>
              </div>
            </div>

            {referralState === 'valid' && referralCode && (
              <div className="register-referral-banner" role="status">
                <span aria-hidden="true">€10</span>
                <div><strong>Je vriendenvoordeel blijft bewaard</strong><p>De korting wordt gekoppeld zodra je registratie klaar is.</p></div>
              </div>
            )}
            {referralState === 'checking' && referralCode && (
              <div className="register-referral-banner register-referral-banner--checking" role="status">
                <span aria-hidden="true">…</span><div><strong>Vriendenlink controleren</strong></div>
              </div>
            )}

            {error && <div className="error-message register-error" role="alert">{error}</div>}

            {credential ? (
              <form onSubmit={handleCompleteGoogleRegistration} className="auth-form register-form">
                <label className="register-consent">
                  <input
                    type="checkbox"
                    checked={acceptedTerms}
                    onChange={(event) => setAcceptedTerms(event.target.checked)}
                    required
                  />
                  <span>
                    Ik ga akkoord met de <Link to="/algemene-voorwaarden?from=google-register">algemene voorwaarden</Link> en het{' '}
                    <Link to="/privacybeleid?from=google-register">privacybeleid</Link>.
                  </span>
                </label>

                <button
                  type="submit"
                  className="register-submit"
                  disabled={isLoading || referralState === 'checking'}
                  aria-busy={isLoading || referralState === 'checking'}
                >
                  {isLoading ? 'Account wordt aangemaakt...' : 'Registratie afronden'}
                  {!isLoading && <span aria-hidden="true">→</span>}
                </button>
              </form>
            ) : (
              <Link to="/register" className="register-submit google-complete-restart">
                Opnieuw registreren
                <span aria-hidden="true">→</span>
              </Link>
            )}
          </section>
        </section>
      </div>
    </PublicLayout>
  );
};
